<script setup lang="ts">
import type { PostSubmit, PreSubmit } from './JobCommandForm.vue';
import {
  CloseOutlined,
  CloudUploadOutlined,
  SaveOutlined,
  SwapOutlined,
} from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { ref } from 'vue';
import { useDevolverCompromisso } from '~/composables/compromissos/useDevolverCompromisso';

interface Props {
  command: string;
  initialModel?: object;
  preResultModel?: PreSubmit;
  preSubmit?: PreSubmit;
  postSubmit?: PostSubmit;
  showUploadFiles?: boolean;
  submitButtonDisabled?: boolean;
  submitButtonText?: string;
  waitForResult?: boolean;
  showDevolverButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showUploadFiles: true,
  devolverButtonVisible: false,
  submitButtonDisabled: false,
  submitButtonText: 'Salvar',
  waitForResult: true,
});

const emit = defineEmits(['success', 'error', 'close']);

const {
  err: devolverErr,
  runAsync: devolverAsync,
  status: devolverStatus,
} = useDevolverCompromisso();

const uploaderStatus = ref<any>(null);
const uploaderRef = ref<any>(null);
const submitStatusRef = ref<'loading' | 'success' | 'error' | null>(null);
const devolverModalVisible = ref(false);
const justificativaDevolucao = ref<any>(null);

async function preResultModel(model: any) {
  submitStatusRef.value = 'loading';

  const canContinue = props.preResultModel
    ? await props.preResultModel(model)
    : true;

  if (!canContinue) {
    submitStatusRef.value = 'error';
    return false;
  }

  if (uploaderRef.value) {
    await uploaderRef.value.upload();
  }

  return true;
}

async function preSubmit(model: any): Promise<boolean> {
  model.pastaCodigos = model.pastaCodigos?.value;
  model.compromissoCodigos = model.compromissoCodigos?.value;
  model.publicacaoNumeroProcessoCodigos =
    model.publicacaoNumeroProcessoCodigos?.value;
  model.andamentoCodigos = model.andamentoCodigos?.value;
  model.arquivos = model.arquivos?.value;
  model.command = { ...model };

  if (props.preSubmit) {
    return await props.preSubmit(model);
  }

  return true;
}

async function postSubmit(status: any, model: any) {
  if (props.postSubmit) {
    await props.postSubmit(status, model);
  }

  submitStatusRef.value = status;
}

async function uiDevolverAsync() {
  const initialModel = props.initialModel as any;

  if (initialModel?.compromissoCodigos?.length !== 1) {
    return;
  }

  await devolverAsync({
    codigoCompromisso: initialModel?.compromissoCodigos[0],
    justificativaDevolucao: justificativaDevolucao.value,
  });

  if (devolverStatus.value === 'error') {
    notification.error({
      description: devolverErr.value.description,
      duration: 5,
      message: devolverErr.value.message,
    });
    emit('error');
    return;
  }

  devolverModalVisible.value = false;

  emit('success');
}

const jobCommandFormRef = ref<any>(null);

defineExpose({
  get model() {
    return jobCommandFormRef.value?.model;
  },
});
</script>

<template>
  <JobCommandFormFinal
    ref="jobCommandFormRef"
    :command="`Compromisso<${props.command}>`"
    :initial-model="props.initialModel"
    :pre-result-model="preResultModel"
    :pre-submit="preSubmit"
    :post-submit="postSubmit"
    :wait-for-result="props.waitForResult"
    @success="() => emit('success')"
    @error="() => emit('success')"
  >
    <template #form="{ model }">
      <slot v-if="devolverStatus !== 'success'" name="form" :model="model" />
      <Uploader
        v-if="props.showUploadFiles && devolverStatus !== 'success'"
        ref="uploaderRef"
        v-model:file-list="model.arquivos"
        style="margin-top: 24px; margin-bottom: 24px"
        @start="() => (uploaderStatus = 'uploading')"
        @end="() => (uploaderStatus = 'done')"
      >
        <template #description>
          <p class="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p class="ant-upload-text">Clique aqui ou arraste os arquivos</p>
          <p class="ant-upload-hint">Envie os arquivos necessários</p>
        </template>
      </Uploader>
      <a-modal
        v-model:visible="devolverModalVisible"
        title="Justifique a devolução"
        cancel-text="Cancelar"
        :z-index="2000"
        :confirm-loading="devolverStatus === 'loading'"
        :ok-button-props="{
          disabled: !justificativaDevolucao?.length,
        }"
        @ok="uiDevolverAsync"
      >
        <a-textarea
          v-model:value="justificativaDevolucao"
          :rows="4"
          placeholder="Escreva sua justificativa"
        />
      </a-modal>
      <a-result
        v-if="devolverStatus === 'success'"
        status="success"
        title="Compromisso devolvido com sucesso"
        sub-title="Agora você pode fechar essa janela"
      >
        <template #extra>
          <a-button @click="() => emit('close')"> Fechar </a-button>
        </template>
      </a-result>
    </template>

    <template #submitSuccess="{ data }">
      <slot name="submitSuccess" :data="data">
        <a-result
          status="success"
          title="Registro realizado com sucesso"
          sub-title="Agora você pode fechar essa janela"
        >
          <template #extra>
            <a-button @click="() => emit('close')"> Fechar </a-button>
          </template>
        </a-result>
      </slot>
    </template>

    <template #submitButton="{ submit }">
      <slot name="submitButton" :submit="submit">
        <div v-if="devolverStatus === 'success'" />
        <a-button
          v-if="devolverStatus !== 'success'"
          :disabled="props.submitButtonDisabled"
          :loading="submitStatusRef === 'loading'"
          type="primary"
          size="large"
          @click.prevent="submit"
        >
          <template #icon>
            <slot name="submitButtonIcon">
              <SaveOutlined />
            </slot>
          </template>
          {{ props.submitButtonText }}
        </a-button>
      </slot>
      <a-button
        v-if="props.showDevolverButton && devolverStatus !== 'success'"
        :loading="devolverStatus === 'loading'"
        size="large"
        style="margin-left: 8px"
        @click="() => (devolverModalVisible = true)"
      >
        <template #icon>
          <SwapOutlined />
        </template>
        Devolver
      </a-button>
      <slot v-if="devolverStatus !== 'success'" name="closeButton">
        <a-button
          size="large"
          style="margin-left: 8px"
          @click="() => emit('close')"
        >
          <template #icon>
            <CloseOutlined />
          </template>
          Fechar
        </a-button>
      </slot>
    </template>
  </JobCommandFormFinal>
</template>
